import React, { useEffect, useMemo, useState } from "react";
import ContentPasteGoIcon from '@mui/icons-material/ContentPasteGo';
import Grid from "@mui/material/Grid2";
import HoverIconButton from "../../ReusedComponents/HoverIconButton";
import { CircularProgress, Box, useTheme, Paper, Typography, Button, Menu, MenuItem, Checkbox } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { DataGridPremium, GridToolbar, GridToolbarColumnsButton, GridToolbarFilterButton, GridToolbarContainer, GridToolbarQuickFilter, useGridApiRef } from '@mui/x-data-grid-premium';
import { GRID_DETAIL_PANEL_TOGGLE_FIELD } from '@mui/x-data-grid-pro';
import { useGridApiContext } from '@mui/x-data-grid-premium';
import { useGridSelector, gridFilterModelSelector } from '@mui/x-data-grid-pro';
import StatusLegend from "./StatusLegend";

// Custom checkbox icons: inline size/visibility; stroke uses CSS var so it's always visible (header/cell set the var)
const iconStyle = {
  width: 24,
  height: 24,
  display: "block",
  opacity: 1,
  visibility: "visible",
  flexShrink: 0,
  stroke: "var(--DataGrid-checkbox-color, #475569)",
  transition: "stroke 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
};
const checkboxStrokeWidth = 2.25;
const CheckboxIconUnchecked = (props) => (
  <svg viewBox="0 0 24 24" style={{ ...iconStyle, ...props.style }} fill="none" strokeWidth={checkboxStrokeWidth} {...props}>
    <rect x={3} y={3} width={18} height={18} rx={3.5} />
  </svg>
);
const CheckboxIconChecked = (props) => (
  <svg viewBox="0 0 24 24" style={{ ...iconStyle, ...props.style }} fill="none" strokeWidth={checkboxStrokeWidth} {...props}>
    <rect x={3} y={3} width={18} height={18} rx={3.5} />
    <path d="M6.5 12l3.5 3.5 7-7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const CheckboxIconIndeterminate = (props) => (
  <svg viewBox="0 0 24 24" style={{ ...iconStyle, ...props.style }} fill="none" strokeWidth={checkboxStrokeWidth} {...props}>
    <rect x={3} y={3} width={18} height={18} rx={3.5} />
    <line x1={7} y1={12} x2={17} y2={12} strokeLinecap="round" />
  </svg>
);

const DataGridCheckbox = React.forwardRef(function DataGridCheckbox(inProps, ref) {
  const { sx, ...props } = inProps;
  return (
    <Checkbox
      ref={ref}
      {...props}
      icon={<CheckboxIconUnchecked />}
      checkedIcon={<CheckboxIconChecked />}
      indeterminateIcon={<CheckboxIconIndeterminate />}
      sx={{
        color: "var(--DataGrid-checkbox-color) !important",
        padding: 0,
        minWidth: 24,
        minHeight: 24,
        borderRadius: "6px",
        transition: "color 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s ease, background-color 0.2s ease",
        "& .MuiSvgIcon-root, & svg": {
          color: "inherit !important",
          fontSize: 24,
        },
        "&.Mui-checked, &.Mui-checked .MuiSvgIcon-root, &.Mui-checked svg": {
          color: "var(--DataGrid-checkbox-checked-color) !important",
          stroke: "var(--DataGrid-checkbox-checked-color) !important",
        },
        "&.MuiCheckbox-indeterminate .MuiSvgIcon-root, &.MuiCheckbox-indeterminate svg": {
          color: "var(--DataGrid-checkbox-checked-color) !important",
          stroke: "var(--DataGrid-checkbox-checked-color) !important",
        },
        ...sx,
      }}
    />
  );
});

// Solid downward-pointing triangle (expand): filled, subtly rounded corners like reference
function ExpandTriangleIcon(props) {
  const { sx, ...rest } = props;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      width="1em"
      height="1em"
      style={{ display: "block", verticalAlign: "middle" }}
      {...rest}
    >
      <path
        d="M6 8 L18 8 L12 18 Z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="0.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Solid upward-pointing triangle (collapse)
function CollapseTriangleIcon(props) {
  const { sx, ...rest } = props;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      width="1em"
      height="1em"
      style={{ display: "block", verticalAlign: "middle" }}
      {...rest}
    >
      <path
        d="M6 16 L18 16 L12 6 Z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="0.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Richer empty state overlay (no rows, not loading) – clear hierarchy and icon
function CustomNoRowsOverlay({ message, description }) {
  const theme = useTheme();
  const isDark = theme.palette?.mode === "dark";
  const textSecondary = theme.palette?.text?.secondary || "#64748b";
  const iconColor = isDark ? "rgba(148, 163, 184, 0.35)" : "rgba(100, 116, 139, 0.35)";
  return (
    <Box
      role="status"
      aria-label="No rows"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 160,
        gap: 1.25,
        px: 2,
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          backgroundColor: iconColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          component="svg"
          aria-hidden
          sx={{ width: 28, height: 28, color: "inherit" }}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
        </Box>
      </Box>
      <Typography variant="body2" sx={{ color: textSecondary, fontWeight: 600, textAlign: "center" }}>
        {message || "No rows"}
      </Typography>
      {description && (
        <Typography variant="caption" sx={{ color: textSecondary, opacity: 0.9, textAlign: "center", maxWidth: 320 }}>
          {description}
        </Typography>
      )}
    </Box>
  );
}

// Export dropdown that calls onExportStart after CSV/Excel export (e.g. for toast)
function CustomExportButton({ csvColumns, disableCsvExportOption, disableExcelExportOption, exportFileName, onExportStart, sx }) {
  const apiRef = useGridApiContext();
  const theme = useTheme();
  const isDark = theme.palette?.mode === "dark";
  const borderColor = theme.palette?.divider || "#e2e8f0";
  const greenColor = theme.palette?.success?.main || "#A0C46D";
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClose = () => setAnchorEl(null);
  const handleCsv = () => {
    if (apiRef.current?.exportDataAsCsv) {
      apiRef.current.exportDataAsCsv({ fileName: exportFileName, fields: csvColumns });
      onExportStart();
    }
    handleClose();
  };
  const handleExcel = () => {
    if (apiRef.current?.exportDataAsExcel) {
      apiRef.current.exportDataAsExcel({ fileName: exportFileName });
      onExportStart();
    }
    handleClose();
  };

  const hideCsv = disableCsvExportOption;
  const hideExcel = disableExcelExportOption;
  if (hideCsv && hideExcel) return null;

  return (
    <>
      <Button
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        endIcon={<KeyboardArrowDownIcon />}
        startIcon={<FileDownloadIcon />}
        aria-controls={open ? "export-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        sx={{
          minHeight: 32,
          minWidth: 40,
          borderRadius: "6px",
          padding: "3px 8px",
          fontSize: "0.75rem",
          textTransform: "none",
          fontWeight: 500,
          backgroundColor: isDark ? "rgba(148, 163, 184, 0.08)" : "#f8fafc",
          color: isDark ? theme.palette?.text?.secondary : "#475569",
          border: `1px solid ${isDark ? "rgba(148, 163, 184, 0.15)" : "rgba(0,0,0,0.06)"}`,
          boxShadow: isDark ? "0 1px 2px rgba(0,0,0,0.15)" : "0 1px 2px rgba(0,0,0,0.04)",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            backgroundColor: isDark ? "rgba(160, 196, 109, 0.12)" : "rgba(160, 196, 109, 0.08)",
            borderColor: greenColor,
            color: isDark ? greenColor : "#7a9c4a",
            boxShadow: isDark ? "0 2px 8px rgba(160, 196, 109, 0.15)" : "0 2px 8px rgba(160, 196, 109, 0.12)",
          },
          ...sx,
        }}
      >
        Export
      </Button>
      <Menu
        id="export-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{ "aria-labelledby": "export-button" }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {!hideCsv && <MenuItem onClick={handleCsv}>Download as CSV</MenuItem>}
        {!hideExcel && <MenuItem onClick={handleExcel}>Download as Excel</MenuItem>}
      </Menu>
    </>
  );
}

// Filter feedback: "N filters active" + Clear all (uses grid context)
function FilterChipsBar({ sx }) {
  const apiRef = useGridApiContext();
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const count = filterModel?.items?.length ?? 0;
  const theme = useTheme();
  const isDark = theme.palette?.mode === "dark";
  const greenColor = theme.palette?.success?.main || "#A0C46D";
  const textSecondary = theme.palette?.text?.secondary || "#64748b";

  if (count === 0) return null;

  const handleClearAll = () => {
    if (apiRef.current) apiRef.current.setFilterModel({ items: [] });
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.625, ...sx }}>
      <Typography variant="caption" sx={{ color: textSecondary, fontWeight: 500, fontSize: "0.75rem" }}>
        {count} filter{count !== 1 ? "s" : ""} active
      </Typography>
      <Button
        size="small"
        onClick={handleClearAll}
        sx={{
          minHeight: 26,
          padding: "0 6px",
          fontSize: "0.75rem",
          textTransform: "none",
          color: greenColor,
          "&:hover": { backgroundColor: isDark ? "rgba(160, 196, 109, 0.12)" : "rgba(160, 196, 109, 0.08)" },
        }}
      >
        Clear all
      </Button>
    </Box>
  );
}

export default function MaterialUiDataGridPremium(props) {
  const theme = useTheme()
  const { palette = {} } = theme;
  const { primary = {}, success = {}, background = {}, text = {}, divider, mode } = palette;
  const isDark = mode === "dark";
  
  // Brand colors
  const navyColor = primary?.main || "#002843";
  const greenColor = success?.main || "#A0C46D";
  const greenLight = isDark ? "rgba(160, 196, 109, 0.12)" : "rgba(160, 196, 109, 0.08)";
  const greenLightHover = isDark ? "rgba(160, 196, 109, 0.18)" : "rgba(160, 196, 109, 0.12)";
  const bgPaper = background?.paper || (isDark ? "#032d42" : "#ffffff");
  const bgDefault = background?.default || (isDark ? "#021e2d" : "#f8fafc");
  const textPrimary = text?.primary || (isDark ? "#ffffff" : "#1e293b");
  const textSecondary = text?.secondary || (isDark ? "#94a3b8" : "#64748b");
  const borderColor = divider || (isDark ? "rgba(148, 163, 184, 0.2)" : "#e2e8f0");
  // Row background colors (used for both row and pinned cells so they match)
  const rowBg = {
    default: bgPaper,
    even: isDark ? "rgba(255, 255, 255, 0.02)" : "#f8fafc",
    tagged: isDark ? "rgba(160, 196, 109, 0.22)" : "rgba(160, 196, 109, 0.2)",
    kitted: isDark ? "rgba(37, 99, 235, 0.22)" : "rgba(37, 99, 235, 0.22)",
  };
  // Opaque equivalents of rowBg (same look, no transparency) for tag/pinned column
  const rowBgOpaque = {
    default: bgPaper,
    even: isDark ? "#08304d" : "#f8fafc",
    tagged: isDark ? "#264e4c" : "#ebf3e2",
    kitted: isDark ? "#0a3970" : "#d3e2ff",
  };
  const pinnedStyles = {
    header: isDark ? "#003d5c" : navyColor,
    border: isDark ? "rgba(148, 163, 184, 0.25)" : "#e2e8f0",
  };

  const classes = {
    container: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "6px 10px",
      backgroundColor: bgPaper,
      borderBottom: `1px solid ${isDark ? "rgba(148, 163, 184, 0.12)" : "rgba(0,0,0,0.06)"}`,
      borderRadius: "12px 12px 0 0",
      flexWrap: "wrap",
      gap: "6px",
      boxShadow: isDark ? "inset 0 1px 0 rgba(255,255,255,0.03)" : "inset 0 1px 0 rgba(255,255,255,0.8)",
    },
    toolbarDataContainer: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      color: textSecondary,
      gap: "8px",
    },
    toolbarData: {
      fontSize: "0.75rem",
      "& .MuiButton-root": {
        minHeight: 32,
        minWidth: 40,
        borderRadius: "6px",
        padding: "3px 8px",
        textTransform: "none",
        fontWeight: 500,
        fontSize: "0.75rem",
        backgroundColor: isDark ? "rgba(148, 163, 184, 0.08)" : "#f8fafc",
        color: isDark ? textSecondary : "#475569",
        border: `1px solid ${isDark ? "rgba(148, 163, 184, 0.15)" : "rgba(0,0,0,0.06)"}`,
        boxShadow: isDark ? "0 1px 2px rgba(0,0,0,0.15)" : "0 1px 2px rgba(0,0,0,0.04)",
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          backgroundColor: greenLight,
          borderColor: greenColor,
          color: isDark ? greenColor : "#7a9c4a",
          boxShadow: isDark ? `0 2px 8px rgba(160, 196, 109, 0.15)` : `0 2px 8px rgba(160, 196, 109, 0.12)`,
        },
        "&:focus-visible": {
          boxShadow: `0 0 0 2px ${bgPaper}, 0 0 0 4px ${greenColor}40`,
        },
      },
    },
  }
  const {
    autosizeColumnsOnMount = false,
    autoHeight = false,
    checkboxSelectedIds = [],
    checkboxSelection = true,
    columns = [],
    columnVisibilityModel: columnVisibilityModelProp,
    customFilters = null,
    customGridToolbar = null,
    delayedLoad = false,
    disableAggregation = true,
    disableColumnFilter = false,
    disableColumnResize = false,
    disableColumnSelector = false,
    disableCsvExportOption = false,
    disableExcelExportOption = true,
    disableRowGrouping = false,
    disableRowSelectionOnClick = false,
    disableSelectionOnClick = true,
    disableToolbar = false,
    emptyStateDescription = "Try adjusting filters or date range.",
    emptyStateMessage = "No rows match your criteria.",

    // Added due to this https://github.com/mui/mui-x/issues/2714
    enableIsRowSelectable = true,
    exportFileName = "",
    exportItemType = "",
    getDetailPanelContent,
    getDetailPanelHeight,
    detailPanelExpandedRowIds,
    onDetailPanelExpandedRowIdsChange,
    getRowClassName = () => { },
    getRowId = () => { },
    getTreeDataPath = () => { },
    statusLegend = [],
    handleConfirmationModal = () => { },
    handleExport = false,
    handleRowSelect = () => { },
    height = "80vh",
    hideFooter = false,
    hideFooterRowCount = false,
    loading = false,
    needsCustomToolbar = false,
    onFilterModelChange = () => { },
    onExportStart = () => { },
    onRowClick = () => { },
    onRowOrderChange = () => { },
    onSortModelChange = () => { },
    filterModel: filterModelProp,
    sortModel: sortModelProp,
    pageSize = 100,
    pagination = true,
    pinnedLeftFields = [],
    rowReordering = false,
    rows = [],
    rowClickable = false,
    selectionModel = undefined,
    showGridToolBar = false,
    state = {},

    sx = {},
    toolBarButtons = () => { },
    toolbarHint = null,
  } = props;

  // Theme-aware DataGrid styles (premium)
  const dataGridStyles = {
    // Container: subtle depth and containment
    backgroundColor: bgPaper,
    borderRadius: "0 0 12px 12px",
    border: "none",
    overflow: "visible",
    boxShadow: isDark ? "inset 0 1px 0 rgba(255,255,255,0.02)" : "inset 0 1px 0 rgba(255,255,255,0.6)",
    // Pinned columns area: opaque so scrollable content doesn't show through
    "--DataGrid-pinnedBackground": bgPaper,
    // Checkbox color in cells (header overrides to white); checked state
    "--DataGrid-checkbox-color": isDark ? "#e2e8f0" : "#64748b",
    "--DataGrid-checkbox-checked-color": greenColor,
    // Keep dropdown attached to tag: no gap in column header row or pinned columns
    "& .MuiDataGrid-pinnedColumns": { gap: 0 },
    // Remove the vertical shadow at the right edge of pinned columns
    "& .MuiDataGrid-scrollShadow": {
      "--opacity": "0",
      opacity: "0 !important",
      pointerEvents: "none",
    },

    // Cell hover effect
    "& .MuiDataGrid-cell:hover": {
      color: isDark ? greenColor : navyColor,
    },
    // Focus visibility (a11y): clear focus ring for keyboard users
    "& .MuiDataGrid-cell:focus-visible": {
      outline: "none",
      boxShadow: `inset 0 0 0 2px ${greenColor}`,
      zIndex: 1,
    },
    "& .MuiDataGrid-columnHeader:focus-visible": {
      outline: "none",
      boxShadow: `inset 0 0 0 2px ${greenColor}`,
    },
    // Reduced motion: respect user preference (single block for all)
    "@media (prefers-reduced-motion: reduce)": {
      "& .MuiDataGrid-row": { transition: "none" },
      "& .MuiDataGrid-cell": { transition: "none" },
      "& .MuiDataGrid-cell:hover": { transition: "none" },
      "& .MuiDataGrid-columnHeader": { transition: "none" },
      "& .MuiDataGrid-overlay": { backdropFilter: "none" },
      "& .MuiDataGrid-virtualScroller": { scrollBehavior: "auto" },
    },
    
    // Cell styling: same padding as headers, refined borders
    "& .MuiDataGrid-cell": {
      borderBottom: `1px solid ${isDark ? "rgba(148, 163, 184, 0.08)" : "rgba(0,0,0,0.04)"}`,
      fontSize: "0.8125rem",
      padding: "7px 10px",
      minHeight: 40,
      boxSizing: "border-box",
      color: textPrimary,
      overflow: "hidden",
      minWidth: 0,
      backgroundColor: "inherit",
      zIndex: 0,
      letterSpacing: "0.01em",
    },
    "& .MuiDataGrid-cellContent": {
      overflow: "hidden",
      minWidth: 0,
      textOverflow: "ellipsis",
    },

    // Row styling: premium hover and selection
    "& .MuiDataGrid-row": {
      gap: 0,
      transition: "background-color 0.2s ease",
      isolation: "isolate",
      backgroundColor: bgPaper,
      cursor: rowClickable ? "pointer" : "default",
      "&:hover": {
        backgroundColor: greenLight,
      },
      "&.Mui-selected": {
        backgroundColor: greenLightHover,
        borderLeft: `3px solid ${greenColor}`,
        boxShadow: "inset 0 0 0 1px rgba(160, 196, 109, 0.15)",
        "&:hover": {
          backgroundColor: isDark ? "rgba(160, 196, 109, 0.22)" : "rgba(160, 196, 109, 0.18)",
        },
      },
    },
    "& .MuiDataGrid-row:nth-of-type(even)": {
      backgroundColor: isDark ? "rgba(255, 255, 255, 0.02)" : "#fafbfc",
      "&:hover": {
        backgroundColor: greenLight,
      },
    },

    // Header: premium bar – apply dark gradient to ALL column headers (pinned + scrollable)
    "& .MuiDataGrid-columnHeaders": {
      borderBottom: "0 !important",
      background: isDark
        ? "linear-gradient(180deg, #003250 0%, #002843 100%)"
        : `linear-gradient(180deg, ${navyColor} 0%, #001a2e 100%)`,
      borderRadius: "0",
      minHeight: "40px !important",
      boxShadow: "inset 0 -1px 0 rgba(0,0,0,0.08)",
    },
    "& .MuiDataGrid-columnHeader": {
      background: isDark
        ? "linear-gradient(180deg, #003250 0%, #002843 100%) !important"
        : `linear-gradient(180deg, ${navyColor} 0%, #001a2e 100%) !important`,
      backgroundColor: "transparent !important",
      color: "rgba(255,255,255,0.95) !important",
      fontWeight: 600,
      fontSize: "0.6875rem",
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      padding: "7px 10px",
      minHeight: "40px !important",
      maxHeight: "40px",
      boxSizing: "border-box",
      transition: "background-color 0.2s ease",
      "&:hover": {
        backgroundColor: "rgba(255,255,255,0.06) !important",
      },
    },
    "& .MuiDataGrid-columnHeaderTitleContainer": {
      justifyContent: "flex-start",
      textAlign: "left",
    },
    "& .MuiDataGrid-columnHeaderRow": {
      minHeight: "40px !important",
      maxHeight: "40px",
      gap: 0,
      display: "flex",
    },

    // Checkbox: premium styling in header (white on dark)
    "& .MuiDataGrid-columnHeader .MuiCheckbox-root": {
      color: "#fff !important",
      padding: 0,
      borderRadius: "6px",
      opacity: "1 !important",
      boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
      transition: "background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s ease",
      "& .MuiSvgIcon-root": {
        color: "#fff !important",
        fill: "currentColor",
        opacity: 1,
      },
      "& path": {
        fill: "currentColor",
      },
      "&.Mui-checked .MuiSvgIcon-root": {
        color: `${greenColor} !important`,
      },
      "&.MuiCheckbox-indeterminate .MuiSvgIcon-root": {
        color: `${greenColor} !important`,
      },
      "&:hover": {
        backgroundColor: "rgba(255,255,255,0.1)",
        boxShadow: "0 2px 4px rgba(0,0,0,0.12)",
      },
      "&:focus-visible": {
        outline: "none",
        boxShadow: `0 0 0 2px ${greenColor}40`,
      },
    },
    "& [data-field=\"__check__\"]": {
      padding: "7px 10px",
      minHeight: 40,
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "visible",
      "& .MuiDataGrid-cellContent": {
        justifyContent: "center",
        overflow: "visible",
        width: "100%",
      },
      "& .MuiCheckbox-root": {
        flexShrink: 0,
        width: 24,
        height: 24,
        padding: 0,
        margin: 0,
        borderRadius: "6px",
        opacity: "1 !important",
        visibility: "visible !important",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: isDark ? "0 1px 2px rgba(0,0,0,0.2)" : "none",
        "& svg": {
          opacity: "1 !important",
          visibility: "visible !important",
          width: "24px !important",
          height: "24px !important",
          display: "block !important",
        },
      },
    },
    // Tracker Type: center pill horizontally and vertically in cell
    "& .MuiDataGrid-cell[data-field=\"assetType\"]": {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "& .MuiDataGrid-cellContent": {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
        minHeight: "100%",
      },
    },
    // Center pill columns (Battery Life only — Last Event matches other text columns)
    "& .MuiDataGrid-cell[data-field=\"batteryLife\"]": {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "& .MuiDataGrid-cellContent": {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        minHeight: "100%",
      },
    },
    // Last Activity (timeModifiedLong): dark mode hover – keep readable color so pill/tooltip don’t clash
    ...(isDark
      ? {
          "& .MuiDataGrid-cell[data-field=\"timeModifiedLong\"]": {
            color: "rgba(226, 232, 240, 0.9) !important",
          },
          "& .MuiDataGrid-cell[data-field=\"timeModifiedLong\"] .MuiSvgIcon-root": {
            color: "rgba(148, 163, 184, 0.65) !important",
          },
          "& .MuiDataGrid-row:nth-of-type(even) .MuiDataGrid-cell[data-field=\"timeModifiedLong\"]": {
            background: "rgba(255, 255, 255, 0.02) !important",
            backgroundColor: "rgba(255, 255, 255, 0.02) !important",
            color: "rgba(226, 232, 240, 0.9) !important",
          },
          "& .MuiDataGrid-row:nth-of-type(even) .MuiDataGrid-cell[data-field=\"timeModifiedLong\"] .MuiSvgIcon-root": {
            color: "rgba(148, 163, 184, 0.65) !important",
          },
          "& .MuiDataGrid-cell[data-field=\"timeModifiedLong\"]:hover": {
            color: "rgba(226, 232, 240, 0.95) !important",
          },
          "& .MuiDataGrid-cell[data-field=\"timeModifiedLong\"]:hover .MuiSvgIcon-root": {
            color: "rgba(148, 163, 184, 0.85) !important",
          },
          "& .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field=\"timeModifiedLong\"]": {
            background: `${greenLight} !important`,
            backgroundColor: `${greenLight} !important`,
            color: "rgba(226, 232, 240, 0.95) !important",
          },
          "& .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field=\"timeModifiedLong\"] .MuiSvgIcon-root": {
            color: "rgba(148, 163, 184, 0.85) !important",
          },
          "& .MuiDataGrid-row:nth-of-type(even):hover .MuiDataGrid-cell[data-field=\"timeModifiedLong\"]": {
            background: `${greenLight} !important`,
            backgroundColor: `${greenLight} !important`,
            color: "rgba(226, 232, 240, 0.95) !important",
          },
          "& .MuiDataGrid-row:nth-of-type(even):hover .MuiDataGrid-cell[data-field=\"timeModifiedLong\"] .MuiSvgIcon-root": {
            color: "rgba(148, 163, 184, 0.85) !important",
          },
        }
      : {}),
    "& .MuiDataGrid-cell .MuiCheckbox-root": {
      color: isDark ? "#e2e8f0 !important" : "#64748b !important",
      padding: 0,
      borderRadius: "6px",
      opacity: "1 !important",
      ...(isDark ? {} : { border: "1px solid #e2e8f0" }),
      transition: "background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), color 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
      "& .MuiSvgIcon-root": {
        color: "inherit !important",
        opacity: 1,
      },
      "& path": {
        fill: "currentColor",
      },
      "&.Mui-checked": {
        color: `${greenColor} !important`,
        ...(isDark ? {} : { borderColor: `${greenColor} !important` }),
      },
      "&.Mui-checked .MuiSvgIcon-root": {
        color: "inherit !important",
      },
      "&:hover": {
        backgroundColor: isDark ? "rgba(160, 196, 109, 0.14)" : "rgba(0,0,0,0.04)",
        boxShadow: isDark ? "0 2px 6px rgba(160, 196, 109, 0.15)" : "none",
        ...(isDark ? {} : { borderColor: "#cbd5e1" }),
      },
      "&:focus-visible": {
        outline: "none",
        boxShadow: isDark ? `0 0 0 2px ${greenColor}40` : "0 0 0 2px rgba(100, 116, 139, 0.25)",
      },
    },

    // Header title and text: always light on dark (all columns)
    "& .MuiDataGrid-columnHeaderTitle": {
      fontWeight: 600,
      color: "rgba(255,255,255,0.95) !important",
    },
    "& .MuiDataGrid-columnHeader .MuiDataGrid-columnHeaderTitleContainerContent": {
      color: "inherit",
    },

    "& .MuiDataGrid-sortIcon": {
      color: "rgba(255,255,255,0.95) !important",
      opacity: 1,
    },
    "& .MuiDataGrid-menuIconButton": {
      color: "rgba(255,255,255,0.8)",
      "&:hover": {
        backgroundColor: "rgba(255,255,255,0.1)",
      },
    },

    // Aggregation label
    "& .MuiDataGrid-aggregationColumnHeaderLabel": {
      color: greenColor,
    },
    
    // Footer: premium bar with soft top edge
    "& .MuiDataGrid-footerContainer": {
      backgroundColor: bgPaper,
      borderTop: `1px solid ${isDark ? "rgba(148, 163, 184, 0.1)" : "rgba(0,0,0,0.06)"}`,
      borderRadius: "0 0 12px 12px",
      padding: "8px 14px",
      minHeight: "48px",
      overflow: "visible",
      position: "relative",
      zIndex: 10,
      boxShadow: isDark ? "0 -4px 12px rgba(0,0,0,0.12)" : "0 -2px 10px rgba(0,0,0,0.03)",
    },
    
    // Pagination: refined controls
    "& .MuiTablePagination-root": {
      color: isDark ? textSecondary : "#475569",
      fontSize: "0.8125rem",
      fontWeight: 500,
      minHeight: 48,
    },
    "& .MuiTablePagination-select": {
      backgroundColor: isDark ? "rgba(148, 163, 184, 0.08)" : "#f8fafc",
      borderRadius: "8px",
      padding: "6px 12px",
      paddingRight: "28px !important",
      border: `1px solid ${isDark ? "rgba(148, 163, 184, 0.15)" : "rgba(0,0,0,0.06)"}`,
      transition: "all 0.2s ease",
      fontWeight: 500,
      fontSize: "0.8125rem",
      color: textPrimary,
      "&:hover": {
        backgroundColor: isDark ? "rgba(148, 163, 184, 0.12)" : "#f1f5f9",
        borderColor: greenColor,
      },
      "&:focus": {
        backgroundColor: bgPaper,
        borderColor: greenColor,
        boxShadow: `0 0 0 2px ${greenLight}`,
      },
    },
    
    "& .MuiTablePagination-selectIcon": {
      color: textSecondary,
    },
    
    "& .MuiTablePagination-menuItem": {
      fontSize: "0.8125rem",
      padding: "8px 14px",
    },
    
    "& .MuiTablePagination-displayedRows": {
      fontWeight: 500,
      fontSize: "0.8125rem",
      color: isDark ? textSecondary : "#475569",
      margin: "0 10px",
    },
    
    "& .MuiTablePagination-actions": {
      marginLeft: "10px",
      "& .MuiIconButton-root": {
        backgroundColor: isDark ? "rgba(148, 163, 184, 0.08)" : "#f8fafc",
        borderRadius: "8px",
        padding: "8px",
        margin: "0 2px",
        minWidth: 40,
        minHeight: 40,
        border: `1px solid ${isDark ? "rgba(148, 163, 184, 0.15)" : "rgba(0,0,0,0.06)"}`,
        color: textSecondary,
        transition: "all 0.2s ease",
        "&:hover": {
          backgroundColor: greenColor,
          borderColor: greenColor,
          color: "#fff",
        },
        "&.Mui-disabled": {
          backgroundColor: isDark ? "rgba(148, 163, 184, 0.05)" : "#f8fafc",
          borderColor: isDark ? "rgba(148, 163, 184, 0.1)" : "rgba(0,0,0,0.06)",
          color: isDark ? "#64748b" : "#cbd5e1",
        },
      },
    },
    
    "& .MuiTablePagination-selectLabel": {
      fontWeight: 500,
      fontSize: "0.8125rem",
      color: textSecondary,
    },
    
    "& .MuiDataGrid-main": {
      borderRadius: "0",
    },
    // Smooth horizontal (and vertical) scrolling
    "& .MuiDataGrid-virtualScroller": {
      scrollBehavior: "smooth",
      WebkitOverflowScrolling: "touch",
      scrollbarColor: isDark ? "rgba(148, 163, 184, 0.4) transparent" : "rgba(0,0,0,0.2) transparent",
      "&::-webkit-scrollbar": { width: 10, height: 10 },
      "&::-webkit-scrollbar-track": { background: "transparent" },
      "&::-webkit-scrollbar-thumb": {
        background: isDark ? "rgba(148, 163, 184, 0.35)" : "rgba(0,0,0,0.18)",
        borderRadius: 5,
        border: "2px solid transparent",
        backgroundClip: "padding-box",
      },
      "&::-webkit-scrollbar-thumb:hover": {
        background: isDark ? "rgba(148, 163, 184, 0.5)" : "rgba(0,0,0,0.28)",
        backgroundClip: "padding-box",
      },
    },
    // Scrollable content: stay behind pinned columns (isolation so pinned z-index wins)
    "& .MuiDataGrid-virtualScrollerContent": {
      position: "relative",
      zIndex: 0,
      isolation: "isolate",
    },
    // Spacer and non-pinned cells: explicitly behind pinned
    "& .MuiDataGrid-cellOffsetLeft": {
      position: "relative",
      zIndex: 0,
    },
    "& .MuiDataGrid-row .MuiDataGrid-cell:not(.MuiDataGrid-cell--pinnedLeft):not(.MuiDataGrid-cell--pinnedRight)": {
      position: "relative",
      zIndex: 0,
    },
    // Pinned left: use same row colors and dimensions as scrollable columns
    // Hide hanging filler — with detail panels it paints a dark strip beside expanded kit rows
    "& .MuiDataGrid-filler--pinnedLeft": {
      display: "none !important",
      width: "0 !important",
      minWidth: "0 !important",
      maxWidth: "0 !important",
      border: "none !important",
      background: "transparent !important",
    },
    "& .MuiDataGrid-pinnedColumns--left": {
      background: "transparent !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-cell--pinnedLeft": {
      zIndex: 100,
      overflow: "hidden !important",
      minHeight: 40,
      boxSizing: "border-box",
      padding: "7px 10px",
      display: "flex",
      alignItems: "center",
      borderBottom: `1px solid ${isDark ? "rgba(148, 163, 184, 0.1)" : "#f1f5f9"}`,
      borderRight: `1px solid ${borderColor}`,
      background: `${rowBgOpaque.default} !important`,
      backgroundColor: `${rowBgOpaque.default} !important`,
    },
    "& .MuiDataGrid-row .MuiDataGrid-cell--pinnedLeft:first-of-type": {
      borderRight: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-row:nth-of-type(even) .MuiDataGrid-cell--pinnedLeft": {
      background: `${rowBgOpaque.even} !important`,
      backgroundColor: `${rowBgOpaque.even} !important`,
    },
    "& .MuiDataGrid-row.row-untagged .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-row.row-tagged .MuiDataGrid-cell--pinnedLeft": {
      background: `${rowBgOpaque.tagged} !important`,
      backgroundColor: `${rowBgOpaque.tagged} !important`,
    },
    "& .MuiDataGrid-row.row-kitted .MuiDataGrid-cell--pinnedLeft": {
      background: `${rowBgOpaque.kitted} !important`,
      backgroundColor: `${rowBgOpaque.kitted} !important`,
    },
    "& .MuiDataGrid-cell--pinnedLeft .MuiDataGrid-cellContent": {
      overflow: "hidden",
      minWidth: 0,
      textOverflow: "ellipsis",
    },
    "& .MuiDataGrid-columnHeaders .MuiDataGrid-cell--pinnedLeft": {
      background: isDark
        ? "linear-gradient(180deg, #003250 0%, #002843 100%) !important"
        : `linear-gradient(180deg, ${navyColor} 0%, #001a2e 100%) !important`,
      backgroundColor: "transparent !important",
      borderRight: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.2)"}`,
      zIndex: 100,
      minHeight: 40,
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
    },
    "& .MuiDataGrid-columnHeaders .MuiDataGrid-cell--pinnedLeft:first-of-type": {
      borderRight: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-row .MuiDataGrid-cell[data-field=\"tag\"]": {
      zIndex: 100,
      flexShrink: 0,
      background: `${rowBgOpaque.default} !important`,
      backgroundColor: `${rowBgOpaque.default} !important`,
      overflow: "hidden !important",
      borderLeft: "none !important",
      borderRight: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-row:nth-of-type(even) .MuiDataGrid-cell[data-field=\"tag\"]": {
      background: `${rowBgOpaque.even} !important`,
      backgroundColor: `${rowBgOpaque.even} !important`,
    },
    "& .MuiDataGrid-row.row-untagged .MuiDataGrid-cell[data-field=\"tag\"], & .MuiDataGrid-row.row-tagged .MuiDataGrid-cell[data-field=\"tag\"]": {
      background: `${rowBgOpaque.tagged} !important`,
      backgroundColor: `${rowBgOpaque.tagged} !important`,
    },
    "& .MuiDataGrid-row.row-kitted .MuiDataGrid-cell[data-field=\"tag\"]": {
      background: `${rowBgOpaque.kitted} !important`,
      backgroundColor: `${rowBgOpaque.kitted} !important`,
    },
    // Tracker Identifier (tag) cell hover – apply row hover color so it matches the rest of the row
    "& .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field=\"tag\"]": {
      background: `${greenLight} !important`,
      backgroundColor: `${greenLight} !important`,
    },
    "& .MuiDataGrid-row:nth-of-type(even):hover .MuiDataGrid-cell[data-field=\"tag\"]": {
      background: `${greenLight} !important`,
      backgroundColor: `${greenLight} !important`,
    },
    "& .MuiDataGrid-row.row-untagged:hover .MuiDataGrid-cell[data-field=\"tag\"], & .MuiDataGrid-row.row-tagged:hover .MuiDataGrid-cell[data-field=\"tag\"]": {
      background: `${isDark ? "rgba(160, 196, 109, 0.3)" : "rgba(160, 196, 109, 0.28)"} !important`,
      backgroundColor: `${isDark ? "rgba(160, 196, 109, 0.3)" : "rgba(160, 196, 109, 0.28)"} !important`,
    },
    "& .MuiDataGrid-row.row-untagged:nth-of-type(even):hover .MuiDataGrid-cell[data-field=\"tag\"], & .MuiDataGrid-row.row-tagged:nth-of-type(even):hover .MuiDataGrid-cell[data-field=\"tag\"]": {
      background: `${isDark ? "rgba(160, 196, 109, 0.3)" : "rgba(160, 196, 109, 0.28)"} !important`,
      backgroundColor: `${isDark ? "rgba(160, 196, 109, 0.3)" : "rgba(160, 196, 109, 0.28)"} !important`,
    },
    "& .MuiDataGrid-row.row-kitted:hover .MuiDataGrid-cell[data-field=\"tag\"]": {
      background: `${isDark ? "rgba(37, 99, 235, 0.32)" : "rgba(37, 99, 235, 0.3)"} !important`,
      backgroundColor: `${isDark ? "rgba(37, 99, 235, 0.32)" : "rgba(37, 99, 235, 0.3)"} !important`,
    },
    "& .MuiDataGrid-row.row-kitted:nth-of-type(even):hover .MuiDataGrid-cell[data-field=\"tag\"]": {
      background: `${isDark ? "rgba(37, 99, 235, 0.32)" : "rgba(37, 99, 235, 0.3)"} !important`,
      backgroundColor: `${isDark ? "rgba(37, 99, 235, 0.32)" : "rgba(37, 99, 235, 0.3)"} !important`,
    },
    // Tracker Identifier (tag) cell selection – match row selected highlight
    "& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell[data-field=\"tag\"]": {
      background: `${greenLightHover} !important`,
      backgroundColor: `${greenLightHover} !important`,
    },
    "& .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell[data-field=\"tag\"]": {
      background: `${isDark ? "rgba(160, 196, 109, 0.22)" : "rgba(160, 196, 109, 0.18)"} !important`,
      backgroundColor: `${isDark ? "rgba(160, 196, 109, 0.22)" : "rgba(160, 196, 109, 0.18)"} !important`,
    },
    "& .MuiDataGrid-columnHeaders [data-field=\"tag\"]": {
      flexShrink: 0,
      background: isDark
        ? "linear-gradient(180deg, #003250 0%, #002843 100%) !important"
        : `linear-gradient(180deg, ${navyColor} 0%, #001a2e 100%) !important`,
      backgroundColor: "transparent !important",
      borderLeft: "none !important",
      borderRight: "none !important",
      zIndex: 100,
      boxShadow: "none !important",
      "&.MuiDataGrid-withBorderColor": {
        borderLeft: "none !important",
        borderRight: "none !important",
      },
    },

    // Checkbox column: same row colors as other columns
    "& .MuiDataGrid-row .MuiDataGrid-cell[data-field=\"__check__\"]": {
      background: `${rowBg.default} !important`,
      backgroundColor: `${rowBg.default} !important`,
    },
    "& .MuiDataGrid-row:nth-of-type(even) .MuiDataGrid-cell[data-field=\"__check__\"]": {
      background: `${rowBg.even} !important`,
      backgroundColor: `${rowBg.even} !important`,
    },
    "& .MuiDataGrid-row.row-untagged .MuiDataGrid-cell[data-field=\"__check__\"], & .MuiDataGrid-row.row-tagged .MuiDataGrid-cell[data-field=\"__check__\"]": {
      background: `${rowBg.tagged} !important`,
      backgroundColor: `${rowBg.tagged} !important`,
    },
    "& .MuiDataGrid-row.row-kitted .MuiDataGrid-cell[data-field=\"__check__\"]": {
      background: `${rowBg.kitted} !important`,
      backgroundColor: `${rowBg.kitted} !important`,
    },
    "& .MuiDataGrid-columnHeaders [data-field=\"__check__\"]": {
      "--DataGrid-checkbox-color": "#fff",
      background: isDark
        ? "linear-gradient(180deg, #003250 0%, #002843 100%) !important"
        : `linear-gradient(180deg, ${navyColor} 0%, #001a2e 100%) !important`,
      backgroundColor: "transparent !important",
      color: "#fff !important",
      padding: "7px 10px",
      minHeight: 40,
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "& .MuiDataGrid-menuIconButton, & .MuiDataGrid-menuIcon": {
        display: "none !important",
      },
      "& .MuiDataGrid-columnHeaderTitleContainer": {
        justifyContent: "center",
        textAlign: "center",
        width: "100%",
        minWidth: 0,
      },
      "& .MuiDataGrid-columnHeaderTitleContainerContent": {
        justifyContent: "center",
      },
      "& .MuiCheckbox-root": {
        color: "#fff !important",
        opacity: "1 !important",
      },
      "& .MuiCheckbox-root .MuiSvgIcon-root": {
        color: "#fff !important",
        fill: "currentColor",
        opacity: "1 !important",
      },
      "& .MuiCheckbox-root path": {
        fill: "currentColor",
      },
      "& .MuiCheckbox-root.Mui-checked .MuiSvgIcon-root": {
        color: `${greenColor} !important`,
      },
    },
    // Ensure checkbox icon is always visible (override theme)
    "& .MuiDataGrid-columnHeader[data-field=\"__check__\"]": {
      "--DataGrid-checkbox-color": "#fff",
    },
    "& .MuiDataGrid-columnHeader[data-field=\"__check__\"] .MuiCheckbox-root": {
      color: "#fff !important",
    },
    "& .MuiDataGrid-columnHeader[data-field=\"__check__\"] .MuiCheckbox-root svg": {
      color: "#fff !important",
      fill: "currentColor",
    },
    "& .MuiDataGrid-columnHeader[data-field=\"__check__\"] .MuiCheckbox-root.Mui-checked svg": {
      color: `${greenColor} !important`,
    },
    "& .MuiDataGrid-cell[data-field=\"__check__\"] .MuiCheckbox-root": {
      color: isDark ? "rgba(255,255,255,0.8)" : "#475569",
    },
    "& .MuiDataGrid-cell[data-field=\"__check__\"] .MuiCheckbox-root.Mui-checked": {
      color: `${greenColor} !important`,
    },
    // Force checkbox SVG to be visible: stroke so outline shows even if fill is overridden
    "& [data-field=\"__check__\"] .MuiCheckbox-root .MuiSvgIcon-root": {
      color: "inherit",
      fill: "currentColor",
    },
    "& [data-field=\"__check__\"] .MuiCheckbox-root svg path": {
      fill: "currentColor",
      stroke: "currentColor",
      strokeWidth: 0.5,
    },
    "& .MuiDataGrid-columnHeader[data-field=\"__check__\"] .MuiCheckbox-root": {
      color: "#fff !important",
    },
    "& .MuiDataGrid-columnHeader[data-field=\"__check__\"] .MuiCheckbox-root .MuiSvgIcon-root": {
      color: "#fff !important",
    },
    "& .MuiDataGrid-cell[data-field=\"__check__\"] .MuiCheckbox-root": {
      color: isDark ? "#e2e8f0 !important" : "#64748b !important",
    },
    "& .MuiDataGrid-cell[data-field=\"__check__\"] .MuiCheckbox-root.Mui-checked": {
      color: `${greenColor} !important`,
    },
    
    "& .MuiDataGrid-columnSeparator": {
      color: isDark ? "rgba(148, 163, 184, 0.25)" : "#e2e8f0",
    },
    // Remove white strip between dropdown and ASSET TAG: hide first column's separator
    "& .MuiDataGrid-columnHeader[data-field=\"__detail_panel_toggle__\"] .MuiDataGrid-columnSeparator": {
      display: "none !important",
    },
    "& .MuiDataGrid-columnHeader[data-field=\"__detail_panel_toggle__\"] .MuiDataGrid-iconSeparator": {
      display: "none !important",
    },
    "& .MuiDataGrid-columnHeader:first-of-type .MuiDataGrid-columnSeparator": {
      display: "none !important",
    },
    "& .MuiDataGrid-columnHeader:first-of-type .MuiDataGrid-iconSeparator": {
      display: "none !important",
    },
    // Hide any separator or shadow element inside dropdown column (header and body)
    "& .MuiDataGrid-columnHeader[data-field=\"__detail_panel_toggle__\"] [class*=\"Separator\"], & .MuiDataGrid-columnHeader[data-field=\"__detail_panel_toggle__\"] [class*=\"separator\"]": {
      display: "none !important",
      width: 0,
      minWidth: 0,
      overflow: "hidden",
    },
    "& .MuiDataGrid-cell[data-field=\"__detail_panel_toggle__\"] [class*=\"Separator\"], & .MuiDataGrid-cell[data-field=\"__detail_panel_toggle__\"] [class*=\"separator\"]": {
      display: "none !important",
    },
    // Force no right border and no shadow on dropdown column (overrides MUI withBorderColor / pinned cell border + shadow)
    "& .MuiDataGrid-columnHeader[data-field=\"__detail_panel_toggle__\"]": {
      borderRight: "none !important",
      borderRightWidth: "0 !important",
      borderRightStyle: "none !important",
      borderRightColor: "transparent !important",
      boxShadow: "none !important",
      outline: "none !important",
      "&::after, &::before": { display: "none !important", boxShadow: "none !important", content: '""' },
    },
    "& .MuiDataGrid-cell[data-field=\"__detail_panel_toggle__\"]": {
      borderRight: "none !important",
      borderRightWidth: "0 !important",
      borderRightStyle: "none !important",
      borderRightColor: "transparent !important",
      boxShadow: "none !important",
      outline: "none !important",
      overflow: "hidden !important",
    },
    // No right border on tag column (header and cells)
    "& .MuiDataGrid-cell.MuiDataGrid-cell--withRightBorder[data-field=\"tag\"]": {
      borderRight: "none !important",
    },
    // Override MUI's cell--withRightBorder when applied to dropdown (theme can add border via this class)
    "& .MuiDataGrid-cell.MuiDataGrid-cell--withRightBorder[data-field=\"__detail_panel_toggle__\"]": {
      borderRight: "none !important",
      boxShadow: "none !important",
    },

    // Loading overlay: clear backdrop
    "& .MuiDataGrid-overlay": {
      backgroundColor: isDark ? "rgba(0, 20, 31, 0.92)" : "rgba(248, 250, 252, 0.92)",
      backdropFilter: "saturate(1.1)",
    },
    
    // Hide footer selected row count (shown in toolbar instead)
    "& .MuiDataGrid-selectedRowCount": {
      display: "none !important",
    },

    // Row status shading: Untagged=green, Tagged(kit)=blue. Use virtualScroller so we beat theme row styles
    // in dark mode (same row must show same color in both themes).
    "& .MuiDataGrid-row.row-untagged, & .MuiDataGrid-row.row-tagged": {
      backgroundColor: `${isDark ? "rgba(160, 196, 109, 0.22)" : "rgba(160, 196, 109, 0.2)"} !important`,
      "&:hover": {
        backgroundColor: `${isDark ? "rgba(160, 196, 109, 0.3)" : "rgba(160, 196, 109, 0.28)"} !important`,
      },
    },
    "& .MuiDataGrid-virtualScroller .MuiDataGrid-row.row-untagged, & .MuiDataGrid-virtualScroller .MuiDataGrid-row.row-tagged": {
      backgroundColor: `${isDark ? "rgba(160, 196, 109, 0.22)" : "rgba(160, 196, 109, 0.2)"} !important`,
      "&:hover": {
        backgroundColor: `${isDark ? "rgba(160, 196, 109, 0.3)" : "rgba(160, 196, 109, 0.28)"} !important`,
      },
    },
    "& .MuiDataGrid-row.row-untagged:nth-of-type(even), & .MuiDataGrid-row.row-tagged:nth-of-type(even), & .MuiDataGrid-virtualScroller .MuiDataGrid-row.row-untagged:nth-of-type(even), & .MuiDataGrid-virtualScroller .MuiDataGrid-row.row-tagged:nth-of-type(even)": {
      backgroundColor: `${isDark ? "rgba(160, 196, 109, 0.22)" : "rgba(160, 196, 109, 0.2)"} !important`,
      "&:hover": {
        backgroundColor: `${isDark ? "rgba(160, 196, 109, 0.3)" : "rgba(160, 196, 109, 0.28)"} !important`,
      },
    },
    "& .MuiDataGrid-row.row-kitted": {
      backgroundColor: `${isDark ? "rgba(37, 99, 235, 0.22)" : "rgba(37, 99, 235, 0.22)"} !important`,
      "&:hover": {
        backgroundColor: `${isDark ? "rgba(37, 99, 235, 0.32)" : "rgba(37, 99, 235, 0.3)"} !important`,
      },
    },
    "& .MuiDataGrid-virtualScroller .MuiDataGrid-row.row-kitted": {
      backgroundColor: `${isDark ? "rgba(37, 99, 235, 0.22)" : "rgba(37, 99, 235, 0.22)"} !important`,
      "&:hover": {
        backgroundColor: `${isDark ? "rgba(37, 99, 235, 0.32)" : "rgba(37, 99, 235, 0.3)"} !important`,
      },
    },
    "& .MuiDataGrid-row.row-kitted:nth-of-type(even), & .MuiDataGrid-virtualScroller .MuiDataGrid-row.row-kitted:nth-of-type(even)": {
      backgroundColor: `${isDark ? "rgba(37, 99, 235, 0.22)" : "rgba(37, 99, 235, 0.22)"} !important`,
      "&:hover": {
        backgroundColor: `${isDark ? "rgba(37, 99, 235, 0.32)" : "rgba(37, 99, 235, 0.3)"} !important`,
      },
    },
    // Cell-level fallback: paint status color on cells (exclude pinned so tag/dropdown stay opaque)
    "& .MuiDataGrid-row.row-untagged .MuiDataGrid-cell:not(.MuiDataGrid-cell--pinnedLeft):not(.MuiDataGrid-cell--pinnedRight), & .MuiDataGrid-row.row-tagged .MuiDataGrid-cell:not(.MuiDataGrid-cell--pinnedLeft):not(.MuiDataGrid-cell--pinnedRight)": {
      backgroundColor: `${isDark ? "rgba(160, 196, 109, 0.22)" : "rgba(160, 196, 109, 0.2)"} !important`,
    },
    "& .MuiDataGrid-row.row-untagged:hover .MuiDataGrid-cell:not(.MuiDataGrid-cell--pinnedLeft):not(.MuiDataGrid-cell--pinnedRight), & .MuiDataGrid-row.row-tagged:hover .MuiDataGrid-cell:not(.MuiDataGrid-cell--pinnedLeft):not(.MuiDataGrid-cell--pinnedRight)": {
      backgroundColor: `${isDark ? "rgba(160, 196, 109, 0.3)" : "rgba(160, 196, 109, 0.28)"} !important`,
    },
    "& .MuiDataGrid-row.row-kitted .MuiDataGrid-cell:not(.MuiDataGrid-cell--pinnedLeft):not(.MuiDataGrid-cell--pinnedRight)": {
      backgroundColor: `${isDark ? "rgba(37, 99, 235, 0.22)" : "rgba(37, 99, 235, 0.22)"} !important`,
    },
    "& .MuiDataGrid-row.row-kitted:hover .MuiDataGrid-cell:not(.MuiDataGrid-cell--pinnedLeft):not(.MuiDataGrid-cell--pinnedRight)": {
      backgroundColor: `${isDark ? "rgba(37, 99, 235, 0.32)" : "rgba(37, 99, 235, 0.3)"} !important`,
    },

    // Detail panel toggle: same row height and alignment; opaque; no right border, no shadow (no line before tag column)
    // flexShrink: 0 keeps it attached to tag column (no gap)
    "& .MuiDataGrid-row [data-field=\"__detail_panel_toggle__\"], & .MuiDataGrid-cell[data-field=\"__detail_panel_toggle__\"]": {
      minWidth: "44px !important",
      width: "44px !important",
      maxWidth: "44px !important",
      flexShrink: 0,
      minHeight: "40px !important",
      padding: "6px 8px",
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 100,
      background: `${rowBgOpaque.default} !important`,
      backgroundColor: `${rowBgOpaque.default} !important`,
      overflow: "hidden",
      borderBottom: `1px solid ${isDark ? "rgba(148, 163, 184, 0.1)" : "#f1f5f9"}`,
      borderRight: "none !important",
      boxShadow: "none !important",
      "& .MuiDataGrid-cellContent": {
        justifyContent: "center",
        alignItems: "center",
      },
      "& .MuiIconButton-root": {
        padding: "4px",
        minWidth: 36,
        minHeight: 36,
        color: isDark ? "#e2e8f0" : "#334155",
        "&:hover": {
          backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
          color: isDark ? greenColor : navyColor,
        },
        "&:focus-visible": {
          outline: "none",
          boxShadow: `0 0 0 2px ${greenColor}`,
        },
        "&.Mui-disabled": {
          color: isDark ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.2)",
        },
      },
      "& .MuiSvgIcon-root": {
        fontSize: "1.1rem",
        transition: "transform 0.2s ease",
        fill: "currentColor",
      },
    },
    "& .MuiDataGrid-row:nth-of-type(even) [data-field=\"__detail_panel_toggle__\"]": {
      background: `${rowBgOpaque.even} !important`,
      backgroundColor: `${rowBgOpaque.even} !important`,
    },
    "& .MuiDataGrid-row.row-untagged [data-field=\"__detail_panel_toggle__\"], & .MuiDataGrid-row.row-tagged [data-field=\"__detail_panel_toggle__\"]": {
      background: `${rowBgOpaque.tagged} !important`,
      backgroundColor: `${rowBgOpaque.tagged} !important`,
    },
    "& .MuiDataGrid-row.row-kitted [data-field=\"__detail_panel_toggle__\"]": {
      background: `${rowBgOpaque.kitted} !important`,
      backgroundColor: `${rowBgOpaque.kitted} !important`,
    },
    "& .MuiDataGrid-row--detailPanelExpanded [data-field=\"__detail_panel_toggle__\"] .MuiIconButton-root": {
      color: greenColor,
    },
    "& .MuiDataGrid-row--detailPanelExpanded [data-field=\"__detail_panel_toggle__\"] .MuiSvgIcon-root": {
      transform: "rotate(180deg)",
    },
    "& .MuiDataGrid-columnHeaders [data-field=\"__detail_panel_toggle__\"]": {
      minWidth: "44px !important",
      width: "44px !important",
      maxWidth: "44px !important",
      flexShrink: 0,
      boxSizing: "border-box",
      zIndex: 100,
      background: isDark
        ? "linear-gradient(180deg, #003250 0%, #002843 100%) !important"
        : `linear-gradient(180deg, ${navyColor} 0%, #001a2e 100%) !important`,
      backgroundColor: "transparent !important",
      borderBottom: "0 !important",
      borderRight: "none !important",
      color: "rgba(255,255,255,0.95)",
      "&.MuiDataGrid-withBorderColor": {
        borderRight: "none !important",
      },
      "& .MuiDataGrid-columnHeaderTitleContainer": {
        justifyContent: "center",
        textAlign: "center",
      },
      "& .MuiDataGrid-iconSeparator": {
        display: "none",
      },
    },
    // Detail panel: clip content; no left accent/shadow that can look like a stray box
    "& .MuiDataGrid-detailPanel": {
      backgroundColor: isDark ? "rgba(0, 40, 67, 0.2)" : "#fafbfc",
      borderBottom: `1px solid ${isDark ? "rgba(148, 163, 184, 0.1)" : "rgba(0,0,0,0.06)"}`,
      transition: "background-color 0.2s ease",
      padding: "8px 12px 10px",
      overflow: "hidden",
      boxShadow: "none",
      borderLeft: "none !important",
      // Detail panel sits under pinned toggle — don't leave a gutter strip
      width: "100%",
      boxSizing: "border-box",
    },
    "& .MuiDataGrid-row--detailPanelExpanded": {
      "& + .MuiDataGrid-detailPanel": {
        borderLeft: "none !important",
        boxShadow: "none !important",
      },
    },
    "& .MuiDataGrid-row--detailPanelExpanded.row-untagged + .MuiDataGrid-detailPanel, & .MuiDataGrid-row--detailPanelExpanded.row-tagged + .MuiDataGrid-detailPanel, & .MuiDataGrid-row--detailPanelExpanded.row-kitted + .MuiDataGrid-detailPanel": {
      borderLeft: "none !important",
    },
    // Scrollbar filler can also hang as a dark block beside detail panels
    "& .MuiDataGrid-scrollbarFiller, & .MuiDataGrid-scrollbarFiller--header, & .MuiDataGrid-scrollbarFiller--borderTop": {
      background: "transparent !important",
      border: "none !important",
    },
    
    // Merge with any passed sx prop
    ...sx,
  };

  const treeDataProp = props.treeData || false;
  const useUseMemoHookProp = props.useUseMemoHook !== undefined ? props.useUseMemoHook : true;
  const hasDetailPanel = typeof getDetailPanelContent === 'function';

  //created to remove the actions column from the export
  const csvColumns = columns.reduce((acc, col) => {
    if (col.field.toLowerCase() !== "actions") {
      acc.push(col.field)
    }
    return acc
  }, [])

  const [pgSize, setPgSize] = useState(pageSize);
  const [page, setPage] = useState(0);
  const [columnLayoutReady, setColumnLayoutReady] = useState(false);
  const apiRef = useGridApiRef();
  let components = {};

  // Check if any column has flex defined - if so, don't autosize unless caller forces it
  const hasFlexColumns = columns.some(col => col.flex !== undefined);
  const shouldAutosize = !hasFlexColumns || autosizeColumnsOnMount;

  // When not autosizing, show grid immediately
  useEffect(() => {
    if (!shouldAutosize) setColumnLayoutReady(true);
  }, [shouldAutosize]);

  // Auto-size columns after data loads so columns fit largest content (headers + cells).
  // Run in rAF so the grid has laid out; then set columnLayoutReady so we show the grid
  // only after resize, avoiding headers/columns visibly jumping from default to autosized widths.
  useEffect(() => {
    if (rows.length === 0 || delayedLoad || !shouldAutosize) {
      if (rows.length === 0 && shouldAutosize) setColumnLayoutReady(true);
      return;
    }
    setColumnLayoutReady(false);
    let rafId = 0;
    let fallbackId = 0;
    const runAutosize = () => {
      if (apiRef.current) {
        apiRef.current.autosizeColumns({
          includeHeaders: true,
          includeOutliers: true,
          expand: true,
        });
        requestAnimationFrame(() => setColumnLayoutReady(true));
      } else {
        fallbackId = requestAnimationFrame(runAutosize);
      }
    };
    rafId = requestAnimationFrame(runAutosize);
    const timeoutId = setTimeout(() => setColumnLayoutReady(true), 400);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (fallbackId) cancelAnimationFrame(fallbackId);
      clearTimeout(timeoutId);
    };
  }, [apiRef, rows, delayedLoad, shouldAutosize]);

  // Detail panel toggle column: force 44px so it aligns with tag (no gap). Default is 40px.
  useEffect(() => {
    if (!hasDetailPanel) return;
    const timer = setTimeout(() => {
      if (apiRef.current?.setColumnWidth) {
        apiRef.current.setColumnWidth(GRID_DETAIL_PANEL_TOGGLE_FIELD, 44);
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [hasDetailPanel, apiRef, rows]);

  // Appends the toolbar
  if (showGridToolBar) {
    // If the parent provides a custom toolbar, we will use that. Otherwise we use the default
    components.Toolbar =
      showGridToolBar && customGridToolbar
        ? customGridToolbar
        : showGridToolBar
          ? GridToolbar
          : null;
  }

  // Appends custom filters
  if (customFilters) {
    components.FilterPanel = customFilters;
  }

  // Custom toolbar with modern styling, selection count, filter chips, export with toast
  function CustomToolbar() {
    const selectionCount = selectionModel == null ? 0 : (Array.isArray(selectionModel) ? selectionModel.length : 0);
    return (
      <GridToolbarContainer sx={classes.container}>
        <Grid sx={classes.toolbarDataContainer}>
          {selectionCount > 0 && (
            <Typography variant="caption" sx={{ color: textSecondary, fontWeight: 600, fontSize: "0.75rem", alignSelf: "center" }}>
              {selectionCount} selected
            </Typography>
          )}
          {toolbarHint}
          <FilterChipsBar />
          <Grid sx={classes.toolbarData}>
            <GridToolbarColumnsButton />
          </Grid>
          <Grid sx={classes.toolbarData}>
            <GridToolbarFilterButton />
          </Grid>
          <Grid sx={classes.toolbarData}>
            <CustomExportButton
              csvColumns={csvColumns}
              disableCsvExportOption={disableCsvExportOption}
              disableExcelExportOption={disableExcelExportOption}
              exportFileName={exportFileName}
              onExportStart={onExportStart}
            />
          </Grid>
          <Grid sx={{
            ...classes.toolbarData,
            minWidth: 0,
            width: 200,
            "& .MuiInputBase-root": {
              minHeight: 32,
              borderRadius: "6px",
              backgroundColor: isDark ? "rgba(148, 163, 184, 0.08)" : "#f8fafc",
              padding: "3px 8px",
              fontSize: "0.75rem",
              border: `1px solid ${isDark ? "rgba(148, 163, 184, 0.15)" : "rgba(0,0,0,0.06)"}`,
              boxShadow: isDark ? "0 1px 2px rgba(0,0,0,0.15)" : "0 1px 2px rgba(0,0,0,0.04)",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                borderColor: isDark ? "rgba(148, 163, 184, 0.25)" : "rgba(0,0,0,0.1)",
              },
              "&.Mui-focused": {
                borderColor: greenColor,
                backgroundColor: bgPaper,
                boxShadow: `0 0 0 2px ${bgPaper}, 0 0 0 4px ${greenLight}`,
              },
            },
            "& .MuiInputBase-input": {
              padding: "2px 0",
              color: textPrimary,
              "&::placeholder": {
                color: textSecondary,
              },
            },
            "& .MuiSvgIcon-root": {
              color: textSecondary,
            },
          }}>
            <GridToolbarQuickFilter 
              debounceMs={500}
              placeholder="Search..."
            />
          </Grid>
        </Grid>
        <Grid sx={classes.toolbarDataContainer}>
          {toolBarButtons()}
        </Grid>
        {handleExport ?
          <Grid sx={classes.toolbarDataContainer}>
            <span>
              <HoverIconButton
                downloadDocType="Excel"
                exportItemType={exportItemType}
                handleClick={() => { handleExport(state, handleConfirmationModal) }}
                icon={<ContentPasteGoIcon />}
                iconDirection="right"
                text="Export"
              />
            </span>
          </Grid>
          :
          null}
      </GridToolbarContainer>
    );
  }

  const hideUntilAutosize = shouldAutosize && !columnLayoutReady;

  return (
    <Box
      sx={{
        opacity: hideUntilAutosize ? 0 : 1,
        transition: "opacity 0.12s ease",
        minHeight: height,
        pointerEvents: hideUntilAutosize ? "none" : "auto",
      }}
    >
    <Paper 
      elevation={0}
      sx={{ 
        height: height,
        borderRadius: "12px",
        overflow: "visible",
        boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.2)" : "0 4px 20px rgba(0,0,0,0.06)",
        border: `1px solid ${borderColor}`,
        backgroundColor: bgPaper,
      }}
    >
      {Array.isArray(statusLegend) && statusLegend.length > 0 && (
        <Box sx={{ px: 1.25, pt: 0.5, pb: 0.375 }}>
          <StatusLegend items={statusLegend} />
        </Box>
      )}
      <DataGridPremium
        apiRef={apiRef}
        autoHeight={autoHeight}
        checkboxSelection={checkboxSelection}
        columnHeaderHeight={40}
        columnVisibilityModel={columnVisibilityModelProp}
        columns={columns}
        detailPanelExpandedRowIds={detailPanelExpandedRowIds}
        disableAggregation={disableAggregation}
        disableRowGrouping={disableRowGrouping}
        disableColumnResize={disableColumnResize}
        disableSelectionOnClick={disableSelectionOnClick}
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={getDetailPanelHeight}
        getRowHeight={() => 40}
        onDetailPanelExpandedRowIdsChange={onDetailPanelExpandedRowIdsChange}
        // following 3 props are used to disable toolbar icons when custom toolbar isnt in use
        disableColumnFilter={disableColumnFilter}
        disableColumnSelector={disableColumnSelector}
        disableDensitySelector
        disableRowSelectionOnClick={disableRowSelectionOnClick}
        experimentalFeatures={{ newEditingApi: true }}
        filterMode="client"
        getRowClassName={getRowClassName}
        /* eslint-disable react-hooks/rules-of-hooks */
        getRowId={useUseMemoHookProp ? useMemo(() => getRowId, []) : getRowId}
        getTreeDataPath={getTreeDataPath}
        hideFooterRowCount={hideFooterRowCount}
        hideFooter={hideFooter}
        keepNonExistentRowsSelected
        autosizeOnMount={shouldAutosize}
        autosizeOptions={shouldAutosize ? {
          includeHeaders: true,
          includeOutliers: true,
          expand: true,
        } : undefined}
        initialState={{
          pinnedColumns: {
            left: [
              ...(hasDetailPanel ? [GRID_DETAIL_PANEL_TOGGLE_FIELD] : []),
              ...(Array.isArray(pinnedLeftFields) ? pinnedLeftFields : []),
            ].filter(Boolean),
            ...(columns.some(col => col.field === "actions") ? { right: ["actions"] } : {}),
          },
          ...(hasDetailPanel ? { detailPanel: { expandedRowIds: [] } } : {}),
          // Only seed sorting when not controlled via sortModel prop
          ...(!sortModelProp
            ? {
                sorting: {
                  sortModel: [{ field: "timeOfLog", sort: "desc" }],
                },
              }
            : {}),
        }}
        {...(filterModelProp !== undefined ? { filterModel: filterModelProp } : {})}
        {...(sortModelProp !== undefined ? { sortModel: sortModelProp } : {})}
        pinnedColumnsSectionSeparator="border"
        isGroupExpandedByDefault={(node) => {
          //if checkbox selection is not possible, then we dont need to check for expanded nodes
          if (!checkboxSelection) { return false }
          const currNode = apiRef?.current?.getRowNode(node.id);
          let isExpanded = false;
          currNode?.children?.some((child) => {
            if (checkboxSelectedIds.includes(child)) {
              isExpanded = true;
              return true; // exit the loop early
            }
            return false;
          });
          return isExpanded;
        }}
        isRowSelectable={enableIsRowSelectable ? (params) => {
          const currRow = apiRef?.current?.getRowNode(params.id);
          if (currRow?.type === 'group') {
            return false;
          }
          return true;
        } : false}
        loading={loading}
        localeText={{
          noRowsLabel: delayedLoad ? <CircularProgress sx={{ color: greenColor }} /> : "No rows",
          labelDisplayedRows: ({ from, to, count }) =>
            count !== -1 ? `${from}–${to} of ${count}` : `${from}–${to} of more than ${to}`,
          ...(hasDetailPanel ? {
            detailPanelToggle: "Expand row",
            expandDetailPanel: "Expand",
            collapseDetailPanel: "Collapse",
          } : {}),
        }}
        onFilterModelChange={(newFilterModel) => {
          onFilterModelChange(newFilterModel);
        }}
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newPageSize) => setPgSize(newPageSize)}
        onRowClick={(rowData) => onRowClick(rowData)}
        onRowOrderChange={(changeInfo) => onRowOrderChange(changeInfo)}
        onRowSelectionModelChange={(selectedRowIds) => {
          handleRowSelect(selectedRowIds);
        }}
        selectionModel={selectionModel}
        onSortModelChange={onSortModelChange}
        page={page}
        pageSize={pgSize}
        pagination={pagination}
        // paginationMode="client"
        // rowCount={rowCountState}
        rowReordering={rowReordering}
        rows={rows}
        rowsPerPageOptions={[25, 50, 100]}
        slots={{
          toolbar: disableToolbar ? null : needsCustomToolbar ? CustomToolbar : GridToolbar,
          noRowsOverlay: CustomNoRowsOverlay,
          baseCheckbox: DataGridCheckbox,
          ...(hasDetailPanel ? {
            detailPanelExpandIcon: ExpandTriangleIcon,
            detailPanelCollapseIcon: CollapseTriangleIcon,
          } : {}),
        }}
        slotProps={{
          baseCheckbox: {},
          noRowsOverlay: { message: emptyStateMessage, description: emptyStateDescription },
          toolbar: {
            csvOptions: { disableToolbarButton: disableCsvExportOption, fields: csvColumns, fileName: exportFileName },
            excelOptions: { disableToolbarButton: disableExcelExportOption, fileName: exportFileName },
            printOptions: { disableToolbarButton: true },
            quickFilterProps: { debounceMs: 500 },
            showQuickFilter: true,
          },
          pagination: {
            labelRowsPerPage: "Rows:",
            SelectProps: {
              native: false,
              MenuProps: {
                PaperProps: {
                  sx: {
                    backgroundColor: bgPaper,
                    border: `1px solid ${borderColor}`,
                    borderRadius: "10px",
                    boxShadow: isDark ? "0 8px 24px rgba(0,0,0,0.35)" : "0 8px 24px rgba(0,0,0,0.1)",
                    marginTop: 1,
                    minWidth: 120,
                    "& .MuiList-root": { padding: "6px 0" },
                    "& .MuiMenuItem-root": {
                      fontSize: "0.875rem",
                      padding: "10px 16px",
                      transition: "background-color 0.15s ease",
                      "&:hover": { backgroundColor: greenLight },
                      "&.Mui-selected": {
                        backgroundColor: greenLightHover,
                        color: isDark ? greenColor : navyColor,
                        "&:hover": { backgroundColor: greenLightHover },
                      },
                    },
                  },
                },
                MenuListProps: {
                  sx: { py: 0.5 },
                },
              },
            },
          },
        }}
        sx={dataGridStyles}
        treeData={treeDataProp}
      />
    </Paper>
    </Box>
  );
}
